import type { ConditionalLogic } from './evaluateConditionalLogic';
import type { SurveyQuestion } from './questionFormatting';

type MissionInput = {
  editParams?: string | null;
  surveyQuestions?: Record<string, unknown>[] | null;
  submissionRules?: string | null;
  socialPlatform?: string | null;
  [key: string]: unknown;
};

// gloss
export type ParsedQuestion = SurveyQuestion & {
  shortQuestion?: string | null;
  recordingMode?: string;
  description?: string | null;
  position?: number;
  compiledQuestion?: string | null;
  conditionalLogic?: ConditionalLogic;
};

// gloss
export type OneClickPostConfig = {
  enabled: boolean;
  postText?: string;
  url?: string;
  shareUrl?: string;
  hashtags?: string;
};

export type MissionContent = {
  surveyQuestions: ParsedQuestion[];
  contentRequirements: string;
  submissionType: string[];
  platformUrls: Record<string, string>;
  reviewPlatforms: string[];
  guidelines: string;
  oneClickPosts: Record<string, OneClickPostConfig>;
  raw: Record<string, string>;
};

// gloss
const emptyContent = (): MissionContent => ({
  surveyQuestions: [],
  contentRequirements: '',
  submissionType: [],
  platformUrls: {},
  reviewPlatforms: [],
  guidelines: '',
  oneClickPosts: {},
  raw: {},
});

export type EditParamsQuestions =
  | { kind: 'questions'; questions: Record<string, unknown>[] }
  | { kind: 'empty' }
  | { kind: 'unreadable' };

// why: surveyQuestions extraction must avoid URLSearchParams; it double-decodes the nested JSON
// why: and corrupts the data.
export const readQuestionsFromEditParams = (editParams: string): EditParamsQuestions => {
  const match = editParams.match(/mission_surveyQuestions=([^&]+)/);
  if (!match?.[1]) return { kind: 'empty' };

  try {
    const decoded = decodeURIComponent(match[1].replace(/\+/g, ' '));
    if (!decoded.startsWith('[')) return { kind: 'unreadable' };
    const parsed = JSON.parse(decoded);
    if (!Array.isArray(parsed)) return { kind: 'unreadable' };
    return parsed.length > 0 ? { kind: 'questions', questions: parsed } : { kind: 'empty' };
  } catch {
    return { kind: 'unreadable' };
  }
};

export const extractQuestionsFromEditParams = (editParams: string): Record<string, unknown>[] | null => {
  const read = readQuestionsFromEditParams(editParams);
  return read.kind === 'questions' ? read.questions : null;
};

// gloss
// why: editParams must go straight to URLSearchParams without pre-decoding; decoding %26 first
// why: truncates values and misdirects review missions (ZLT-3086).
const parseEditParamsRaw = (editParams: string): Record<string, string> => {
  try {
    const result: Record<string, string> = {};
    for (const [key, value] of new URLSearchParams(editParams).entries()) {
      result[key] = value;
    }
    return result;
  } catch {
    return {};
  }
};

// gloss
export const safeJsonParse = <T>(value: string | undefined, fallback: T): T => {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

// gloss
const normalizeApiQuestions = (questions: Record<string, unknown>[]): ParsedQuestion[] =>
  questions.map((q) => {
    let options: unknown[] = [];
    if (q.options) {
      options = Array.isArray(q.options) ? q.options : [];
    } else if (q.questionOptions) {
      try {
        options =
          typeof q.questionOptions === 'string' ? JSON.parse(q.questionOptions) : (q.questionOptions as unknown[]);
      } catch {
        options = [];
      }
    }

    return {
      id: (q.surveyID ?? q.id) as number | undefined,
      uuid: (q.uuid as string) || null,
      text: (q.text as string) || (q.question as string) || '',
      type: (q.type as string) || (q.questionType as string) || 'text',
      options,
      scale: q.scale ? String(q.scale) : '10',
      isRequired: Boolean(q.isRequired ?? true),
      shortQuestion: (q.shortQuestion as string) || (q.compiledQuestion as string) || null,
      recordingMode: (q.recordingMode as string) || undefined,
      description: (q.description as string) || null,
      position: q.position as number | undefined,
      compiledQuestion: (q.compiledQuestion as string) || null,
      conditionalLogic: (q.conditionalLogic as ConditionalLogic) ?? null,
    };
  });

// gloss
export const parseMissionContent = (mission: MissionInput | null | undefined): MissionContent => {
  if (!mission) return emptyContent();

  let surveyQuestions: ParsedQuestion[] = [];

  if (Array.isArray(mission.surveyQuestions) && mission.surveyQuestions.length > 0) {
    surveyQuestions = normalizeApiQuestions(mission.surveyQuestions);
  } else if (mission.editParams) {
    const fromParams = extractQuestionsFromEditParams(mission.editParams);
    if (fromParams) {
      surveyQuestions = fromParams
        .map((q) => normalizeApiQuestions([q])[0])
        .filter((q): q is ParsedQuestion => q != null);
    }
  }

  const raw = mission.editParams ? parseEditParamsRaw(mission.editParams) : {};

  const contentRequirements =
    (mission.submissionRules as string) || raw.mission_contentRequirements || raw.mission_submissionRules || '';

  const submissionType = safeJsonParse<string[]>(raw.mission_submissionType, []);

  const platformUrls = safeJsonParse<Record<string, string>>(raw.mission_platformUrls, {});

  let reviewPlatforms: string[] = [];
  if (mission.socialPlatform && typeof mission.socialPlatform === 'string' && mission.socialPlatform.trim()) {
    reviewPlatforms = mission.socialPlatform.split(',').filter(Boolean);
  } else {
    const fromParams =
      raw.mission_selectedPlatforms || raw.mission_selectedReviewPlatforms || raw.selectedPlatforms || '';
    if (fromParams) {
      reviewPlatforms = safeJsonParse<string[]>(fromParams, fromParams.split(',').filter(Boolean));
    }
  }

  const oneClickPosts = safeJsonParse<Record<string, OneClickPostConfig>>(raw.mission_oneClickPosts, {});

  return {
    surveyQuestions,
    contentRequirements,
    submissionType,
    platformUrls,
    reviewPlatforms,
    guidelines: contentRequirements,
    oneClickPosts,
    raw,
  };
};
